'use client';

import { useState } from 'react';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import Button from '@/components/atoms/button';

// EWM-299 (OQ-5) — list name max length, mirrored by the BFF
// (validateWishlistName). Keep the two in sync.
const NAME_MAX_LENGTH = 50;

interface RenameListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  currentDescription: string;
  onSubmit: (name: string, description: string) => Promise<boolean>;
  // Names of the customer's OTHER lists (excluding the one being renamed),
  // used for the inline duplicate-name check (trim + case-insensitive).
  // Excluding self means keeping the current name is always allowed.
  existingNames?: string[];
}

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-14 text-primary placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';
const labelClass = 'text-13 mb-2 block text-left font-semibold text-primary';

const RenameListDrawer = ({
  isOpen,
  onClose,
  currentName,
  currentDescription,
  onSubmit,
  existingNames = [],
}: RenameListDrawerProps) => {
  const translate = useTranslations('dashboard');
  const translateCommon = useTranslations('common');
  const [name, setName] = useState(currentName);
  const [description, setDescription] = useState(currentDescription);
  const [submitting, setSubmitting] = useState(false);

  // EWM-760 (was a setState-in-effect on `[isOpen, currentName,
  // currentDescription]`) — adjust-state-during-render: reset to the current
  // list's name/description whenever the drawer opens or the target list
  // changes. Each prior dependency gets its own tracker so the render-time
  // check fires on exactly the same set of changes as the old effect's array,
  // with the same `if (isOpen)` guard.
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [prevCurrentName, setPrevCurrentName] = useState(currentName);
  const [prevCurrentDescription, setPrevCurrentDescription] = useState(currentDescription);
  if (prevIsOpen !== isOpen || prevCurrentName !== currentName || prevCurrentDescription !== currentDescription) {
    setPrevIsOpen(isOpen);
    setPrevCurrentName(currentName);
    setPrevCurrentDescription(currentDescription);
    if (isOpen) {
      setName(currentName);
      setDescription(currentDescription);
    }
  }

  const trimmedName = name.trim();
  const isDuplicate =
    trimmedName.length > 0 &&
    existingNames.some((existing) => existing.trim().toLowerCase() === trimmedName.toLowerCase());
  const canSubmit = trimmedName.length > 0 && !isDuplicate;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const ok = await onSubmit(trimmedName, description.trim());
    setSubmitting(false);
    if (ok) onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} headline={translate('purchase-list-rename')} direction="right">
      <form onSubmit={handleSubmit} noValidate className="flex min-h-full flex-col">
        <div className="flex-1 px-6 py-6">
          <p className="text-13 mb-6 text-gray-500">{translate('purchase-list-rename-description')}</p>

          <div className="flex flex-col gap-5">
            <div>
              <label htmlFor="rename-list-name" className={labelClass}>
                {translateCommon('name')} *
              </label>
              <input
                id="rename-list-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={NAME_MAX_LENGTH}
                aria-invalid={isDuplicate}
                aria-describedby={isDuplicate ? 'rename-list-name-error' : undefined}
                className={inputClass}
              />
              {isDuplicate && (
                <p id="rename-list-name-error" className="text-12 mt-1 text-red-600">
                  {translate('purchase-list-name-duplicate')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="rename-list-desc" className={labelClass}>
                {translateCommon('description')}
              </label>
              <textarea
                id="rename-list-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={`${inputClass} resize-none`}
              />
              <p className="text-12 mt-1 text-gray-400">{translate('purchase-list-description-hint')}</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button
            type="submit"
            variant="primary"
            size="m"
            className="w-full"
            loading={submitting}
            disabled={!canSubmit}
          >
            {translateCommon('save')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

export default RenameListDrawer;
